import { HttpClient } from '@angular/common/http';
import { DestroyRef, inject, Injectable, model, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RequestsDtoV2 } from '@banyan/commons/dtos/requests.dto';
import { Catalog } from '@banyan/commons/models/admin/catalog.model';
import { UpdateProduct } from '@banyan/commons/models/admin/update-product.model';
import { ProductV2 } from '@banyan/commons/models/order/product.model';
import { RequestItem } from '@banyan/commons/models/request/request-item.model';
import { RequestDetails } from '@banyan/commons/models/request-details.model';
import { RequestHistory } from '@banyan/commons/models/request-history.model';
import { RequestsResponseV2 } from '@banyan/commons/models/requests-response.model';
import { ProgressSpinnerService } from '@banyan/modules/shared/services/progress-spinner/progress-spinner.service';
import { AlertComponent } from '@banyan/modules/standalone/alert/alert.component';
import { environment } from '@env/environment';

@Injectable({
  providedIn: 'root',
})
export class AdminService {
  private readonly PATH: string = '/admin';
  progressSpinnerService = inject(ProgressSpinnerService);
  httpClient = inject(HttpClient);
  destroyRef = inject(DestroyRef);
  snackBar = inject(MatSnackBar);
  requests = signal<RequestsResponseV2 | null>(null);
  requestsLoaded = signal<boolean>(false);
  isLoadingRequest = signal<boolean>(false);
  request = signal<RequestDetails | null>(null);
  requestComment = model<string | null>(null);
  searchedProducts = signal<ProductV2[]>([]);
  isLoadingProducts = signal<boolean>(false);
  selectedProducts = signal<ProductV2[]>([]);
  volumeChoices = signal<RequestItem[]>([{
    code: '5-20',
    name: '5 - 20 kg',
  },
    {
      code: '25-50',
      name: '25 - 50 kg',
    },
    {
      code: '75+',
      name: '75+ kg',
    },
    {
      code: 'NA',
      name: "I don't know",
    },
  ]);
  newProducts = signal<UpdateProduct[]>([]);
  updatedProducts = signal<UpdateProduct[]>([]);
  deletedProducts = signal<number[]>([]);
  snapshotProducts = signal<ProductV2[]>([]);
  canSubmit = signal<boolean>(false);
  isEditing = signal<boolean>(false);
  isProductChange = signal<boolean>(false);
  requestHistory = signal<RequestHistory | null>(null);
  catalog = signal<Catalog | null>(null);
  catalogLoaded = signal<boolean>(false);
  MAX_SELECTED_PRODUCTS: number = 99;

  getRequests(limit: number, offset: number): void {
    this.progressSpinnerService.show();
    this.requestsLoaded.set(false);
    this.httpClient.get<RequestsResponseV2>(`${environment.middlewareUrl}${this.PATH}/requests?limit=${limit}&offset=${offset}`)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.requests.set(RequestsDtoV2(data));
          this.requestsLoaded.set(true);
          this.progressSpinnerService.hide();
        },
        error: () => {
          this.requestsLoaded.set(false);
          this.progressSpinnerService.hide();
        },
      });
  }

  initData(): void {
    this.isEditing.set(false);
    this.canSubmit.set(false);
    this.requestComment.set(null);
    this.selectedProducts.set([]);
    this.searchedProducts.set([]);
    this.newProducts.set([]);
    this.updatedProducts.set([]);
    this.deletedProducts.set([]);
    this.snapshotProducts.set([]);
  }

  getCatalog(limit: number, page: number): void {
    this.progressSpinnerService.show();
    this.catalogLoaded.set(false);
    this.httpClient.get<Catalog>(`${environment.middlewareUrl}${this.PATH}/catalog/products?limit=${limit}&page=${page}`)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.catalog.set(data);
          this.catalogLoaded.set(true);
          this.progressSpinnerService.hide();
        },
        error: () => {
          this.catalogLoaded.set(false);
          this.progressSpinnerService.hide();
        },
      });
  }

  getRequest(id: number, endUseId: number): void {
    this.initData();
    this.isLoadingRequest.set(true);
    this.progressSpinnerService.show();
    this.httpClient
      .get<any>(
        `${environment.middlewareUrl}${this.PATH}/requests/${id}?endUseId=${endUseId}`
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {
          if (data) {
            this.getRequestHistory(id);
            this.request.set(data);
            if (data.fragranceProfiles?.length > 0) {
              const firstFragranceProfile = data.fragranceProfiles[0];
              if (firstFragranceProfile.products?.length > 0) {
                this.snapshotProducts.set([...firstFragranceProfile.products]);
                this.selectedProducts.set([...firstFragranceProfile.products]);
                this.canSubmit.set(true);
              }
            }
            this.isLoadingRequest.set(false);
            this.progressSpinnerService.hide();
          }
        },
        error: () => {
          this.isLoadingRequest.set(false);
          this.progressSpinnerService.hide();
        },
      });
  }

  saveRequestProduct(
    requestId: number,
    endUseId: number,
    briefId: number,
    fragranceProfileId: number
  ): void {
    const params = {
      briefId,
      fragranceProfileId,
      newProducts: this.newProducts(),
      updatedProducts: this.updatedProducts(),
      deletedProducts: this.deletedProducts(),
    };
    this.progressSpinnerService.show();
    this.httpClient
      .patch<any>(
        `${environment.middlewareUrl}${this.PATH}/requests/${requestId}`,
        params
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.getRequest(requestId, endUseId);
          this.handleSubmissionSuccess(
            'Saved successfully, the proposals are ready for submission.'
          );
        },
        error: () => {
          this.canSubmit.set(false);
          this.handleSubmissionError(
            'An error occurred while saving your request.'
          );
        },
      });
  }

  searchProducts(keywords: string): void {
    const param = { keywords: keywords };
    this.httpClient
      .post<any>(`${environment.middlewareUrl}${this.PATH}/products`, param)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {
          this.searchedProducts.set(data.products ?? []);
          this.isLoadingProducts.set(false);
        },
        error: () => {
          this.searchedProducts.set([]);
          this.isLoadingProducts.set(false);
        },
      });
  }

  checkAddedProducts(products: ProductV2[]): void {
    products.forEach(product => {
      if (this.removeFromDeletedProducts(product)) {
        this.selectedProducts().push(product);
        return;
      }

      if (
        !this.isProductInList(product, this.snapshotProducts()) &&
        !this.isProductInList(product, this.newProducts())
      ) {
        const updatedProduct: UpdateProduct = this.buildUpdateProduct(product);

        this.newProducts().push(updatedProduct);
        this.selectedProducts().push(product);
        this.canSubmit.set(false);
      }
      if (this.isProductInList(product, this.snapshotProducts())) {
        const productSnapshot = this.getProductFromList(
          product,
          this.snapshotProducts()
        );
        if (productSnapshot) {
          if (this.removeFromDeletedProducts(productSnapshot)) {
            this.selectedProducts().push(productSnapshot);
          }
        }
      }
    });
  }

  private buildUpdateProduct(product: ProductV2): UpdateProduct {
    return {
      id: product.id,
      price: product.price?.value,
      bigCommerceProductId: product.bigCommerceProductId,
      minQuantity: product.minQuantity,
      description: product.description,
      imageUrl: product.imageUrl,
      name: product.name,
      isWaitingForReview: product.isWaitingForReview,
    };
  }

  checkRemovedProduct(product: ProductV2): void {
    if (
      this.isProductInList(product, this.snapshotProducts()) &&
      !this.isDeletedProduct(product) &&
      product.id
    ) {
      this.deletedProducts().push(product.id);
    }
    this.removeFromList(product, this.newProducts());
    this.removeFromList(product, this.updatedProducts());
    this.removeFromList(product, this.selectedProducts());
    this.checkChangesProducts();
  }

  checkUpdatedProduct(product: ProductV2): void {
    if (this.isProductInList(product, this.snapshotProducts())) {
      this.updateOrAddProduct(product, this.updatedProducts());
      this.removeFromList(product, this.newProducts());
    } else {
      this.updateOrAddProduct(product, this.newProducts());
    }
    this.updateOrAddProductV2(product, this.selectedProducts());
    this.checkChangesProducts();
  }

  getProductFromList(
    product: ProductV2,
    list: ProductV2[]
  ): ProductV2 | undefined {
    return list.find(
      p => p.bigCommerceProductId === product.bigCommerceProductId
    );
  }

  private checkChangesProducts() {
    if (this.snapshotProducts().length > 0) {
      if (
        JSON.stringify(this.selectedProducts()) ===
        JSON.stringify(this.snapshotProducts())
      ) {
        this.canSubmit.set(true);
      } else {
        this.canSubmit.set(false);
      }
    }
  }

  private isProductInList(
    product: ProductV2,
    list: ProductV2[] | UpdateProduct[]
  ): boolean {
    return list.some(
      p => p.bigCommerceProductId === product.bigCommerceProductId
    );
  }

  private removeFromList(
    product: ProductV2,
    list: ProductV2[] | UpdateProduct[]
  ): void {
    const index = list.findIndex(
      p => p.bigCommerceProductId === product.bigCommerceProductId
    );
    if (index !== -1) list.splice(index, 1);
  }

  private updateOrAddProduct(product: ProductV2, list: UpdateProduct[]): void {
    const index = list.findIndex(
      p => p.bigCommerceProductId === product.bigCommerceProductId
    );

    const updateProduct: UpdateProduct  = {
      ...product,
      price: product.price?.value,
    };


    if (index !== -1) {
      list[index] = updateProduct;
    } else {
      list.push(updateProduct);
    }
  }

  private updateOrAddProductV2(product: ProductV2, list: ProductV2[]): void {
    const index = list.findIndex(
      p => p.bigCommerceProductId === product.bigCommerceProductId
    );

    if (index !== -1) {
      list[index] = product;
    } else {
      list.push(product);
    }
  }

  private isDeletedProduct(product: ProductV2): boolean {
    return this.deletedProducts().includes(product.id ?? 0);
  }

  private removeFromDeletedProducts(product: ProductV2): boolean {
    const index = this.deletedProducts().findIndex(id => id === product.id);
    if (index !== -1) {
      this.deletedProducts().splice(index, 1);
      return true;
    }
    return false;
  }

  submitRequest(requestId: number, endUseId: number): void {
    this.progressSpinnerService.show();
    this.httpClient.post<any>(
      `${environment.middlewareUrl}${this.PATH}/requests/${this.request()?.id}/submit`,
      null
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.handleSubmissionSuccess('The proposal has been submitted.');
          this.getRequest(requestId, endUseId);
        },
        error: () => {
        this.handleSubmissionError('An error occurred while submitting your proposal.');
        }
      })
  }

  handleSubmissionSuccess(text: string): void {
    this.showSnackBar('success', text);
    this.progressSpinnerService.hide();
  }

  handleSubmissionError(text: string): void {
    this.showSnackBar('error', text);
    this.progressSpinnerService.hide();
  }

  showSnackBar(type: string, text: string): void {
    const params = {
      type,
      text,
      icon: 'info',
      closeButton: true,
    };
    this.snackBar.openFromComponent(AlertComponent, {
      data: params,
      duration: 3000,
      verticalPosition: 'bottom',
      horizontalPosition: 'center',
      panelClass: 'custom-position'
    });
  }

  saveComment(): void {
    this.progressSpinnerService.show();
    this.httpClient.post<any>(
      `${environment.middlewareUrl}${this.PATH}/requests/${this.request()?.id}/comments`,
      {comment: this.requestComment()}
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.handleSubmissionSuccess(
            'The comment has been saved successfully.'
          );
          this.requestComment.set(null);
          this.getRequestHistory(this.request()?.id ?? 0);
          this.progressSpinnerService.hide();
        },
        error: () => {
          this.handleSubmissionError('An error occurred while saving your comment.');
        }
      });
  }

  getRequestHistory(id: number): void {
    this.progressSpinnerService.show();
    this.httpClient.get<any>(
      `${environment.middlewareUrl}${this.PATH}/requests/${id}/history`,
    ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        {
          next: (data) => {
            if (data) {
              this.requestHistory.set(data);
              this.progressSpinnerService.hide();
            }
          },
          error: () => {
            this.progressSpinnerService.hide();
          },
        });
  }
}
